import {
  useEffect,
  useState
} from 'react';

import {
  isNativeApp
} from '../utils/nativeApp';

import {
  getNativeDeviceToken
} from '../utils/nativeDeviceAuth';


const SUPABASE_URL =
  process.env
    .REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env
    .REACT_APP_SUPABASE_PUBLISHABLE_KEY;


function headers(
  extra = {}
) {
  return {
    apikey:
      SUPABASE_KEY,

    Authorization:
      'Bearer ' +
      SUPABASE_KEY,

    ...extra
  };
}


function numberOrNull(value) {
  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return null;
  }

  const number =
    Number(value);

  return Number.isFinite(number)
    ? number
    : null;
}


async function readJson(
  response,
  label
) {
  const text =
    await response.text();

  if (!response.ok) {
    throw new Error(
      `${label} failed (${response.status}): ${text}`
    );
  }

  return text
    ? JSON.parse(text)
    : null;
}


async function fetchPlacement(
  courseId,
  streamNumber,
  groupNumber
) {
  if (
    !courseId ||
    streamNumber === null
  ) {
    return {
      streamName: '',
      groupName: '',
      groupDescription: ''
    };
  }

  const encodedCourse =
    encodeURIComponent(
      courseId
    );

  const streamRequest =
    fetch(
      `${SUPABASE_URL}/rest/v1/course_stream_labels?course_id=eq.${encodedCourse}&stream_number=eq.${streamNumber}&select=name&limit=1`,
      {
        headers:
          headers()
      }
    );

  const groupRequest =
    groupNumber === null
      ? Promise.resolve(null)
      : fetch(
          `${SUPABASE_URL}/rest/v1/course_group_labels?course_id=eq.${encodedCourse}&stream_number=eq.${streamNumber}&group_number=eq.${groupNumber}&select=name,description&limit=1`,
          {
            headers:
              headers()
          }
        );

  const [
    streamResponse,
    groupResponse
  ] =
    await Promise.all([
      streamRequest,
      groupRequest
    ]);

  const streamRows =
    await readJson(
      streamResponse,
      'Loading Stream title'
    );

  let groupRows = [];

  if (groupResponse) {
    groupRows =
      await readJson(
        groupResponse,
        'Loading Group information'
      );
  }

  return {
    streamName:
      String(
        streamRows?.[0]?.name ||
        ''
      ).trim(),

    groupName:
      String(
        groupRows?.[0]?.name ||
        ''
      ).trim(),

    groupDescription:
      String(
        groupRows?.[0]
          ?.description ||
        ''
      ).trim()
  };
}


async function renameOwnGroup({
  session,
  courseId,
  streamNumber,
  groupNumber,
  name
}) {
  let deviceToken = null;

  if (isNativeApp()) {
    deviceToken =
      await getNativeDeviceToken();
  }

  const webToken =
    !isNativeApp()
      ? session
          ?.webSessionToken ||
        null
      : null;

  if (
    !deviceToken &&
    !webToken
  ) {
    throw new Error(
      'Please sign out and sign in again before renaming your Group.'
    );
  }

  const response =
    await fetch(
      `${SUPABASE_URL}/rest/v1/rpc/student_rename_course_group`,
      {
        method: 'POST',

        headers:
          headers({
            'Content-Type':
              'application/json'
          }),

        body:
          JSON.stringify({
            p_course_id:
              courseId,

            p_stream_number:
              streamNumber,

            p_group_number:
              groupNumber,

            p_name:
              name,

            p_web_token:
              webToken,

            p_device_token:
              deviceToken
          })
      }
    );

  const result =
    await readJson(
      response,
      'Renaming Group'
    );

  if (!result?.ok) {
    throw new Error(
      result?.message ||
      'The Group could not be renamed.'
    );
  }

  return result;
}


export default function CoursePlacement({
  session,
  enrolment,
  variant = 'light'
}) {
  const courseId =
    enrolment?.course_id ||
    enrolment?.courses?.id ||
    null;

  const streamNumber =
    numberOrNull(
      enrolment?.stream
    );

  const groupNumber =
    numberOrNull(
      enrolment
        ?.group_number
    );

  const [streamName, setStreamName] =
    useState('');

  const [groupName, setGroupName] =
    useState('');

  const [
    groupDescription,
    setGroupDescription
  ] =
    useState('');

  const [editing, setEditing] =
    useState(false);

  const [draft, setDraft] =
    useState('');

  const [saving, setSaving] =
    useState(false);

  const [error, setError] =
    useState('');


  useEffect(() => {
    let cancelled = false;

    setError('');

    fetchPlacement(
      courseId,
      streamNumber,
      groupNumber
    )
      .then(result => {
        if (cancelled) {
          return;
        }

        setStreamName(
          result.streamName
        );

        setGroupName(
          result.groupName
        );

        setGroupDescription(
          result.groupDescription
        );
      })
      .catch(err => {
        console.error(
          'Loading course placement failed:',
          err
        );

        if (!cancelled) {
          setError(
            'Could not load Stream / Group names.'
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    courseId,
    streamNumber,
    groupNumber
  ]);


  if (
    streamNumber === null &&
    groupNumber === null
  ) {
    return null;
  }


  const dark =
    variant === 'hero';

  const streamTitle =
    streamNumber === null
      ? ''
      : (
          streamName ||
          `Stream ${streamNumber}`
        );

  const groupTitle =
    groupNumber === null
      ? ''
      : (
          groupName ||
          `Group ${groupNumber}`
        );


  function beginRename() {
    setDraft(
      groupName || ''
    );

    setError('');
    setEditing(true);
  }


  async function saveRename(
    event
  ) {
    event.preventDefault();

    if (
      !courseId ||
      streamNumber === null ||
      groupNumber === null
    ) {
      return;
    }

    setSaving(true);
    setError('');

    try {
      const result =
        await renameOwnGroup({
          session,
          courseId,
          streamNumber,
          groupNumber,
          name: draft
        });

      setGroupName(
        String(
          result.name ||
          ''
        ).trim()
      );

      setGroupDescription(
        String(
          result.description ||
          ''
        ).trim()
      );

      setEditing(false);

    } catch (err) {
      setError(
        err?.message ||
        'The Group could not be renamed.'
      );

    } finally {
      setSaving(false);
    }
  }


  const textColor =
    dark
      ? '#ffffff'
      : '#486581';

  const secondaryColor =
    dark
      ? '#d9e2ec'
      : '#627d98';


  return (
    <div
      style={{
        margin:
          dark
            ? '-1px 0 8px'
            : '5px 0 9px',

        color:
          textColor,

        fontSize:
          '12px',

        lineHeight:
          1.35
      }}
    >
      {streamTitle && (
        <div
          style={{
            fontWeight:
              700,

            marginBottom:
              groupNumber !== null
                ? '3px'
                : 0
          }}
        >
          {streamTitle}
        </div>
      )}


      {groupNumber !== null && (
        <>
          {!editing ? (
            <div
              style={{
                display:
                  'flex',

                alignItems:
                  'center',

                gap:
                  '8px',

                flexWrap:
                  'wrap'
              }}
            >
              <strong>
                {groupTitle}
              </strong>

              {!session?.isMaster && (
                <button
                  type="button"
                  onClick={
                    beginRename
                  }
                  style={{
                    border:
                      dark
                        ? '1px solid rgba(255,255,255,0.45)'
                        : '1px solid #bcccdc',

                    borderRadius:
                      '4px',

                    background:
                      dark
                        ? 'rgba(255,255,255,0.10)'
                        : '#ffffff',

                    color:
                      textColor,

                    padding:
                      '2px 6px',

                    fontSize:
                      '10px',

                    fontWeight:
                      700,

                    cursor:
                      'pointer'
                  }}
                >
                  Rename
                </button>
              )}
            </div>

          ) : (
            <form
              onSubmit={
                saveRename
              }
              style={{
                display:
                  'flex',

                gap:
                  '5px',

                alignItems:
                  'center',

                flexWrap:
                  'wrap'
              }}
            >
              <input
                value={draft}
                maxLength={80}
                autoFocus
                placeholder={
                  `Group ${groupNumber}`
                }
                onChange={
                  event =>
                    setDraft(
                      event
                        .target
                        .value
                    )
                }
                style={{
                  minWidth:
                    '160px',

                  border:
                    '1px solid #bcccdc',

                  borderRadius:
                    '4px',

                  padding:
                    '5px 7px',

                  fontSize:
                    '12px',

                  color:
                    '#243b53',

                  background:
                    '#ffffff'
                }}
              />

              <button
                disabled={saving}
                style={{
                  border: 0,
                  borderRadius:
                    '4px',
                  padding:
                    '5px 8px',
                  background:
                    '#216e45',
                  color:
                    '#ffffff',
                  fontSize:
                    '11px',
                  fontWeight:
                    700,
                  cursor:
                    'pointer'
                }}
              >
                {saving
                  ? 'Saving…'
                  : 'Save'}
              </button>

              <button
                type="button"
                disabled={saving}
                onClick={() =>
                  setEditing(false)
                }
                style={{
                  border:
                    dark
                      ? '1px solid rgba(255,255,255,0.45)'
                      : '1px solid #bcccdc',

                  borderRadius:
                    '4px',

                  padding:
                    '4px 7px',

                  background:
                    'transparent',

                  color:
                    textColor,

                  fontSize:
                    '11px',

                  cursor:
                    'pointer'
                }}
              >
                Cancel
              </button>
            </form>
          )}


          {groupDescription && (
            <div
              style={{
                marginTop:
                  '2px',

                color:
                  secondaryColor,

                fontSize:
                  '11px'
              }}
            >
              {groupDescription}
            </div>
          )}
        </>
      )}


      {error && (
        <div
          style={{
            marginTop:
              '4px',

            color:
              dark
                ? '#ffd2d2'
                : '#b42318',

            fontSize:
              '11px'
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}